import { useAuth } from "src/contexts/auth-provider";
import { useFormik } from "formik";
import { useState, forwardRef, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { EventFormProps } from "src/interfaces/event-props.interface";
import { useAddEvent } from "../hooks/useAddEvent";
import { useUpdateEvent } from "../hooks/useUpdateEvent";
import { useCategoriesWithItems } from "../hooks/useCategoriesWithItems";
import Button from "src/components/common/Button";
import FormRowVertical from "src/components/common/FormRowVertical";
import Input, { InputTypeEnum, InputAsEnum } from "src/components/common/Input";
import SearchBar from "src/components/common/SearchBar";
import CustomSelect from "src/components/common/CustomSelect";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
// Import custom date picker styles AFTER default styles to override them
import "../date-picker.css";
import { eventSchema } from "../event.schema";
import infoIcon from "src/assests/images/dashboard/info.svg";
import { ICONS } from "src/assests";
import { convertDollarToCents } from "src/helpers";
import { DiscountApplyTypeEnum } from "src/enums/order.enum";
import { AddEventItemInput, AddEventRecurrenceInput, UpdateEventItemInput, UpdateEventRecurrenceInput } from "src/interfaces/event.interface";
import { MenuItem } from "src/interfaces/menu.interface";
import { failureToast } from "src/utils/toasts";

const EventForm = ({
  eventToEdit,
  handleCloseEventModal,
  onEventsMutated,
}: EventFormProps) => {
  const { hasRestaurant } = useAuth();
  const { addEvent, isAddingEvent } = useAddEvent();
  const { updateEvent, isUpdatingEvent } = useUpdateEvent();
  const { filteredItems, searchItems, allItems } = useCategoriesWithItems();

  const buttonText = eventToEdit ? "Update" : "Save";
  const isLoadingEvent = isAddingEvent || isUpdatingEvent;

  const [recurrenceFrequency, setRecurrenceFrequency] = useState<string>("Daily");
  const [selectedDays, setSelectedDays] = useState<{ [key: string]: boolean }>({
    Monday: false,
    Tuesday: false,
    Wednesday: false,
    Thursday: false,
    Friday: false,
    Saturday: false,
    Sunday: false,
  });

  const daysOfWeek = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0, show: false });
  const [searchValue, setSearchValue] = useState<string>("");
  const [showItemDropdown, setShowItemDropdown] = useState<boolean>(false);
  const searchBarRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchBarRef.current && !searchBarRef.current.contains(event.target as Node)) {
        setShowItemDropdown(false);
      }
    };

    if (showItemDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showItemDropdown]);

  // Populate eventItems, recurrence frequency, and selected days from eventToEdit
  useEffect(() => {
    if (eventToEdit?.eventItems && eventToEdit.eventItems.length > 0) {
      const formattedItems = eventToEdit.eventItems.map((item: any) => {
        const finalPrice = (item.finalPriceCents || 0) / 100;
        
        // Determine discount type from the item data
        let discountType = "Fixed";
        let discountPercentage = "";
        let discountFixedPrice = "";
        
        if (item.discountType === "PERCENTAGE" || item.discountPercent) {
          discountType = "Percentage";
          discountPercentage = item.discountPercent?.toString() || "";
        } else if (item.discountType === "FIXED" || item.discountFixedPriceCents) {
          discountType = "Fixed";
          discountFixedPrice = ((item.discountFixedPriceCents || 0) / 100).toFixed(2);
        }

        // Look up item name and base price from allItems using itemCorrelationId
        const menuItem = allItems.find((menuItem: any) => menuItem.correlationId === item.itemCorrelationId);
        const itemName = menuItem?.englishName || "Item";
        const basePrice = (menuItem?.priceCents || 0) / 100;

        return {
          id: Date.now() + Math.random(), // Unique ID
          itemName,
          basePrice,
          discountType,
          discountPercentage,
          discountFixedPrice,
          finalPrice,
          correlationId: item.itemCorrelationId || "", // Use itemCorrelationId as correlationId
          eventItemCorrelationId: item.correlationId || "", // Store the event item correlation ID for updates
        };
      });
      
      setEventItems(formattedItems);
    } else if (!eventToEdit) {
      // Reset if no event to edit
      setEventItems([]);
    }
  }, [eventToEdit, allItems]);

  // Populate recurrence frequency and selected days from eventToEdit
  useEffect(() => {
    if (eventToEdit?.recurrences && eventToEdit.recurrences.length > 0) {
      const recurrence = eventToEdit.recurrences[0];
      const recurrenceRule = recurrence.recurrenceRule || "";
      
      if (recurrenceRule.includes("FREQ=DAILY")) {
        setRecurrenceFrequency("Daily");
        setSelectedDays({
          Monday: false,
          Tuesday: false,
          Wednesday: false,
          Thursday: false,
          Friday: false,
          Saturday: false,
          Sunday: false,
        });
      } else if (recurrenceRule.includes("FREQ=WEEKLY")) {
        setRecurrenceFrequency("Weekly");
        
        // Parse BYDAY from recurrence rule (e.g., "FREQ=WEEKLY;BYDAY=MO,WE,FR")
        const byDayMatch = recurrenceRule.match(/BYDAY=([^;]+)/);
        if (byDayMatch) {
          const daysAbbr = byDayMatch[1].split(",");
          const dayMap: { [key: string]: string } = {
            MO: "Monday",
            TU: "Tuesday",
            WE: "Wednesday",
            TH: "Thursday",
            FR: "Friday",
            SA: "Saturday",
            SU: "Sunday",
          };
          
          const newSelectedDays: { [key: string]: boolean } = {
            Monday: false,
            Tuesday: false,
            Wednesday: false,
            Thursday: false,
            Friday: false,
            Saturday: false,
            Sunday: false,
          };
          
          daysAbbr.forEach((abbr: string) => {
            const day = dayMap[abbr.trim()];
            if (day) {
              newSelectedDays[day] = true;
            }
          });
          
          setSelectedDays(newSelectedDays);
        }
      }
    } else if (!eventToEdit) {
      // Reset if no event to edit
      setRecurrenceFrequency("Daily");
      setSelectedDays({
        Monday: false,
        Tuesday: false,
        Wednesday: false,
        Thursday: false,
        Friday: false,
        Saturday: false,
        Sunday: false,
      });
    }
  }, [eventToEdit]);

  // Event items table
  const [eventItems, setEventItems] = useState<Array<{
    id: number;
    itemName: string;
    basePrice: number;
    discountType: string;
    discountPercentage: string;
    discountFixedPrice: string;
    finalPrice: number;
    correlationId?: string;
    eventItemCorrelationId?: string; // For update: existing event item correlationId
  }>>([]);

  const handleDayToggle = (day: string) => {
    if (recurrenceFrequency === "Weekly") {
      setSelectedDays(prev => ({
        ...prev,
        [day]: !prev[day]
      }));
    }
  };

  // Custom trigger for DatePicker. The chevron makes date/time fields read as selectors, not blank text inputs.
  const DateCustomInput = forwardRef<HTMLButtonElement, {
    value?: string;
    onClick?: () => void;
    placeholder?: string;
  }>(
    ({ value, onClick, placeholder }, ref) => (
      <button
        type="button"
        className="mt-[6px] flex h-[42px] w-full items-center justify-between gap-2 rounded-[8px] border border-[#F1F1F2] bg-white px-[16px] text-left text-sm text-[#252525] transition-colors hover:border-[#D1D5DB] focus:outline-none focus:ring-2 focus:ring-[#FD7657]/20"
        onClick={onClick}
        ref={ref}
      >
        <span className={value ? "truncate" : "truncate text-[#9B9B9B]"}>
          {value || placeholder}
        </span>
        <svg className="h-4 w-4 shrink-0 text-[#9B9B9B]" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
          <path
            fillRule="evenodd"
            d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
            clipRule="evenodd"
          />
        </svg>
      </button>
    )
  );
  DateCustomInput.displayName = "DateCustomInput";
  
  // Helper functions to parse date string (YYYY-MM-DD) or fallback to Unix timestamp
  const parseDateFromEvent = (dateString?: string, seconds?: number): Date | null => {
    if (dateString) {
      // Parse date string in YYYY-MM-DD format
      const date = new Date(dateString + 'T00:00:00');
      if (!isNaN(date.getTime())) {
        return date;
      }
    }
    // Fallback: if no date string, try to parse from Unix timestamp
    if (seconds) {
      const date = new Date(seconds * 1000);
      date.setHours(0, 0, 0, 0);
      return date;
    }
    return null;
  };

  // Helper function to parse time from seconds (seconds from midnight 0:00:00)
  const parseTimeFromSeconds = (seconds?: number): Date | null => {
    if (!seconds && seconds !== 0) return null;
    // seconds represents seconds from midnight (0:00:00)
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    // Use today's date but with the parsed time
    const today = new Date();
    today.setHours(hours, minutes, secs, 0);
    return today;
  };

  // Helper function to format date as YYYY-MM-DD string
  const formatDateToString = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Helper function to calculate seconds from midnight (0:00:00) of a day
  const calculateSecondsFromMidnight = (time: Date): number => {
    const hours = time.getHours();
    const minutes = time.getMinutes();
    const seconds = time.getSeconds();
    return hours * 3600 + minutes * 60 + seconds;
  };

  const formik = useFormik<{
    eventName: string;
    eventDescription: string;
    startDate: Date | null;
    startTime: Date | null;
    endDate: Date | null;
    endTime: Date | null;
    recurrenceFrequency: string;
    isActive: boolean;
  }>({
    initialValues: {
      eventName: eventToEdit?.eventName || "",
      eventDescription: eventToEdit?.eventDescription || "",
      startDate: parseDateFromEvent(eventToEdit?.startDate, eventToEdit?.startTimeInSeconds),
      startTime: parseTimeFromSeconds(eventToEdit?.startTimeInSeconds),
      endDate: parseDateFromEvent(eventToEdit?.endDate, eventToEdit?.endTimeInSeconds),
      endTime: parseTimeFromSeconds(eventToEdit?.endTimeInSeconds),
      recurrenceFrequency: "Daily",
      isActive: eventToEdit ? eventToEdit.active !== false : true,
    },
    validationSchema: eventSchema,
    onSubmit: async (values, { resetForm }) => {
      if (!values.startDate || !values.startTime || !values.endDate || !values.endTime) {
        return;
      }

      // Format dates as YYYY-MM-DD strings
      const startDateString = formatDateToString(values.startDate);
      const endDateString = formatDateToString(values.endDate);
      
      // Calculate seconds from midnight (0:00:00) for times
      const startTimeInSeconds = calculateSecondsFromMidnight(values.startTime);
      const endTimeInSeconds = calculateSecondsFromMidnight(values.endTime);

      // Format event items for mutation
      const formattedEventItems: AddEventItemInput[] = eventItems
        .filter((item) => item.correlationId) // Only include items with valid correlationId
        .map((item) => {
        const finalPriceCents = convertDollarToCents(item.finalPrice);
        
        const eventItem: AddEventItemInput = {
          itemCorrelationId: item.correlationId || "",
          discountType: item.discountType === "Percentage" ? DiscountApplyTypeEnum.PERCENTAGE : DiscountApplyTypeEnum.FIXED,
          finalPriceCents,
        };

        if (item.discountType === "Percentage") {
          // Convert percentage string to number (e.g., "10" -> 10 for BigDecimal)
          const discountPercent = parseFloat(item.discountPercentage) || 0;
          if (discountPercent > 0) {
            eventItem.discountPercent = discountPercent;
          }
        } else if (item.discountType === "Fixed") {
          // Convert fixed price to cents
          const discountFixedPriceCents = convertDollarToCents(item.discountFixedPrice);
          if (discountFixedPriceCents > 0) {
            eventItem.discountFixedPriceCents = discountFixedPriceCents;
          }
        }

        return eventItem;
      });

      // Format recurrences for mutation using iCalendar RRULE standard
      const formattedRecurrences: AddEventRecurrenceInput[] = [];
      
      if (recurrenceFrequency === "Daily") {
        // Daily recurrence - iCalendar RRULE format
        formattedRecurrences.push({
          recurrenceRule: "FREQ=DAILY",
        });
      } else if (recurrenceFrequency === "Weekly") {
        // Weekly recurrence - iCalendar RRULE format
        const dayAbbreviations: { [key: string]: string } = {
          Monday: "MO",
          Tuesday: "TU",
          Wednesday: "WE",
          Thursday: "TH",
          Friday: "FR",
          Saturday: "SA",
          Sunday: "SU",
        };

        const selectedDayAbbreviations = daysOfWeek
          .filter(day => selectedDays[day])
          .map(day => dayAbbreviations[day]);

        if (selectedDayAbbreviations.length > 0) {
          // Create a weekly recurrence rule with selected days using iCalendar RRULE format
          formattedRecurrences.push({
            recurrenceRule: `FREQ=WEEKLY;BYDAY=${selectedDayAbbreviations.join(",")}`,
          });
        } else {
          // If weekly is selected but no days are selected, default to all days
          formattedRecurrences.push({
            recurrenceRule: "FREQ=WEEKLY",
          });
        }
      }

      if (eventToEdit?.correlationId) {
        // Format update event items - only include items with valid itemCorrelationId
        const formattedUpdateEventItems: UpdateEventItemInput[] = eventItems
          .filter((item) => item.correlationId && item.correlationId.trim() !== "") // Only include items with valid correlationId
          .map((item) => {
            const finalPriceCents = convertDollarToCents(item.finalPrice);
            
            const updateEventItem: UpdateEventItemInput = {
              // Use existing eventItemCorrelationId if available (from eventToEdit.eventItems), otherwise empty string will be handled by backend
              eventItemCorrelationId: item.eventItemCorrelationId || (eventToEdit.eventItems?.find(
                (ei: any) => ei.itemCorrelationId === item.correlationId
              )?.correlationId || ""),
              itemCorrelationId: item.correlationId,
              discountType: item.discountType === "Percentage" ? DiscountApplyTypeEnum.PERCENTAGE : DiscountApplyTypeEnum.FIXED,
              finalPriceCents,
            };

            if (item.discountType === "Percentage") {
              const discountPercent = parseFloat(item.discountPercentage) || 0;
              if (discountPercent > 0) {
                updateEventItem.discountPercent = discountPercent;
              }
            } else if (item.discountType === "Fixed") {
              const discountFixedPriceCents = convertDollarToCents(item.discountFixedPrice);
              if (discountFixedPriceCents > 0) {
                updateEventItem.discountFixedPriceCents = discountFixedPriceCents;
              }
            }

            return updateEventItem;
          })
          .filter((item) => item.eventItemCorrelationId && item.eventItemCorrelationId.trim() !== ""); // Only include items with valid eventItemCorrelationId

        // Format update event recurrences - always include recurrence frequency
        // Use existing recurrence correlationId if available, otherwise try to match by pattern
        const formattedUpdateRecurrences: UpdateEventRecurrenceInput[] = [];
        
        if (formattedRecurrences.length > 0) {
          formattedRecurrences.forEach((recurrence) => {
            // Try to find matching existing recurrence
            // First try exact match by rule
            let existingRecurrence = eventToEdit.recurrences?.find(
              (er: any) => er.recurrenceRule === recurrence.recurrenceRule
            );
            
            // If no exact match, try to find by frequency type (Daily vs Weekly)
            if (!existingRecurrence && eventToEdit.recurrences && eventToEdit.recurrences.length > 0) {
              const isDaily = recurrence.recurrenceRule.includes("FREQ=DAILY");
              const isWeekly = recurrence.recurrenceRule.includes("FREQ=WEEKLY");
              
              existingRecurrence = eventToEdit.recurrences.find((er: any) => {
                if (isDaily && er.recurrenceRule.includes("FREQ=DAILY")) {
                  return true;
                }
                if (isWeekly && er.recurrenceRule.includes("FREQ=WEEKLY")) {
                  return true;
                }
                return false;
              });
            }
            
            // If we found an existing recurrence, update it
            if (existingRecurrence?.correlationId && existingRecurrence.correlationId.trim() !== "") {
              formattedUpdateRecurrences.push({
                eventRecurrentCorrelationId: existingRecurrence.correlationId,
                recurrenceRule: recurrence.recurrenceRule,
              });
            } else if (eventToEdit.recurrences && eventToEdit.recurrences.length > 0) {
              // If we have existing recurrences but no match, update the first one
              formattedUpdateRecurrences.push({
                eventRecurrentCorrelationId: eventToEdit.recurrences[0].correlationId,
                recurrenceRule: recurrence.recurrenceRule,
              });
            }
            // If no existing recurrences, the backend should handle creating new ones
          });
          
          // Always ensure we have at least one recurrence entry if formattedRecurrences exists
          if (formattedUpdateRecurrences.length === 0 && formattedRecurrences.length > 0 && eventToEdit.recurrences && eventToEdit.recurrences.length > 0) {
            // Fallback: update the first existing recurrence
            formattedUpdateRecurrences.push({
              eventRecurrentCorrelationId: eventToEdit.recurrences[0].correlationId,
              recurrenceRule: formattedRecurrences[0].recurrenceRule,
            });
          }
        }

        // Only call updateEvent if we have valid storeCorrelationId
        if (!hasRestaurant?.correlationId) {
          failureToast("Store correlation ID is missing");
          return;
        }

        const updated = await updateEvent({
          eventCorrelationId: eventToEdit.correlationId,
          storeCorrelationId: hasRestaurant.correlationId,
          eventName: values.eventName,
          eventDescription: values.eventDescription || undefined,
          startDate: startDateString,
          endDate: endDateString,
          startTimeInSeconds,
          endTimeInSeconds,
          eventItems: formattedUpdateEventItems.length > 0 ? formattedUpdateEventItems : undefined,
          recurrences: formattedUpdateRecurrences.length > 0 ? formattedUpdateRecurrences : undefined,
          isActive: values.isActive,
        });
        if (!updated) return;
      } else {
        const added = await addEvent({
          storeCorrelationId: hasRestaurant?.correlationId || "",
          eventName: values.eventName,
          eventDescription: values.eventDescription || undefined,
          startDate: startDateString,
          endDate: endDateString,
          startTimeInSeconds,
          endTimeInSeconds,
          eventItems: formattedEventItems.length > 0 ? formattedEventItems : undefined,
          recurrences: formattedRecurrences.length > 0 ? formattedRecurrences : undefined,
        });
        if (!added) return;
      }
      await onEventsMutated?.();
      // Reset form and event items
      setEventItems([]);
      setSearchValue("");
      setSelectedDays({
        Monday: false,
        Tuesday: false,
        Wednesday: false,
        Thursday: false,
        Friday: false,
        Saturday: false,
        Sunday: false,
      });
      setRecurrenceFrequency("Daily");
      handleCloseEventModal();
      resetForm();
    },
    enableReinitialize: true,
  });

  return (
      <div className="flex min-h-0 w-full min-w-0 flex-1 flex-col overflow-hidden">
        <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-contain pr-1 sm:pr-2">
        {/* First row: Event Name and Description */}
        <div className="mb-2 flex w-full min-w-0 flex-col gap-4 sm:flex-row">
          <div className="min-w-0 flex-1">
            <FormRowVertical label="Event Name" required={true} error={formik.touched.eventName && formik.errors.eventName ? formik.errors.eventName : ""}>
              <Input
                type={InputTypeEnum.TEXT}
                {...formik.getFieldProps("eventName")}
                className="h-[42px]"
              />
            </FormRowVertical>
          </div>
          <div className="min-w-0 flex-1">
            <FormRowVertical label="Description" error={formik.touched.eventDescription && formik.errors.eventDescription ? formik.errors.eventDescription : ""}>
              <Input
                type={InputTypeEnum.TEXT}
                {...formik.getFieldProps("eventDescription")}
                className="h-[42px]"
              />
            </FormRowVertical>
          </div>
        </div>

        {eventToEdit ? (
          <div className="mb-3">
            <FormRowVertical label="Event status" optional compact>
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={formik.values.isActive !== false}
                  onChange={(e) => formik.setFieldValue("isActive", e.target.checked)}
                  className="receipt-lang-check w-6 h-6 min-w-[24px] min-h-[24px] shrink-0 rounded border border-[#9B9B9B] cursor-pointer appearance-none"
                />
                <span className="text-sm">Event is active (visible to customers when scheduled)</span>
              </label>
            </FormRowVertical>
          </div>
        ) : null}
        
        {/* Second row: Start Date, End Date, Start Time, End Time */}
        <div className="mb-2 grid w-full min-w-0 grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <div className="min-w-0">
            <FormRowVertical label="Start Date" required={true} error={formik.touched.startDate && formik.errors.startDate ? formik.errors.startDate : ""}>
              <DatePicker
                selected={formik.values.startDate}
                onChange={(date: Date | null) => {
                  formik.setFieldValue("startDate", date);
                  formik.setFieldTouched("startDate", true, false);
                }}
                dateFormat="MM/dd/yyyy"
                customInput={<DateCustomInput placeholder="Select date" />}
                className="w-full"
              />
            </FormRowVertical>
          </div>
          <div className="min-w-0">
            <FormRowVertical label="End Date" required={true} error={formik.touched.endDate && formik.errors.endDate ? formik.errors.endDate : ""}>
              <DatePicker
                selected={formik.values.endDate}
                onChange={(date: Date | null) => {
                  formik.setFieldValue("endDate", date);
                  formik.setFieldTouched("endDate", true, false);
                }}
                dateFormat="MM/dd/yyyy"
                customInput={<DateCustomInput placeholder="Select date" />}
                className="w-full"
                minDate={formik.values.startDate || undefined}
              />
            </FormRowVertical>
          </div>
          <div className="min-w-0">
            <FormRowVertical label="Start Time" required={true} error={formik.touched.startTime && formik.errors.startTime ? formik.errors.startTime : ""}>
              <DatePicker
                selected={formik.values.startTime}
                onChange={(time: Date | null) => {
                  formik.setFieldValue("startTime", time);
                  formik.setFieldTouched("startTime", true, false);
                }}
                showTimeSelect
                showTimeSelectOnly
                timeIntervals={15}
                dateFormat="h:mm aa"
                timeCaption="Time"
                customInput={<DateCustomInput placeholder="Select time" />}
                className="w-full"
              />
            </FormRowVertical>
          </div>
          <div className="min-w-0">
            <FormRowVertical label="End Time" required={true} error={formik.touched.endTime && formik.errors.endTime ? formik.errors.endTime : ""}>
              <DatePicker
                selected={formik.values.endTime}
                onChange={(time: Date | null) => {
                  formik.setFieldValue("endTime", time);
                  formik.setFieldTouched("endTime", true, false);
                }}
                showTimeSelect
                showTimeSelectOnly
                timeIntervals={15}
                dateFormat="h:mm aa"
                timeCaption="Time"
                customInput={<DateCustomInput placeholder="Select time" />}
                className="w-full"
              />
            </FormRowVertical>
          </div>
        </div>

        {/* Fourth row: Recurrence Frequency */}
        <div className="mb-4 flex w-full min-w-0 flex-col gap-4 lg:flex-row lg:items-start">
          <div className="min-w-0 flex-1 lg:max-w-md">
            <FormRowVertical label="Recurrence Frequency" required={true}>
              <CustomSelect
                options={[
                  { value: "Daily", label: "Daily" },
                  { value: "Weekly", label: "Weekly" },
                ]}
                value={recurrenceFrequency}
                onChange={(value) => {
                  setRecurrenceFrequency(value);
                  formik.setFieldValue("recurrenceFrequency", value);
                  // If Daily is selected, clear all day selections
                  if (value === "Daily") {
                    setSelectedDays({
                      Monday: false,
                      Tuesday: false,
                      Wednesday: false,
                      Thursday: false,
                      Friday: false,
                      Saturday: false,
                      Sunday: false,
                    });
                  }
                }}
                placeholder="Select frequency"
                label="Recurrence Frequency"
              />
            </FormRowVertical>
          </div>
          <div className="flex min-w-0 flex-1 flex-wrap content-center gap-x-4 gap-y-2 lg:pt-8">
            {daysOfWeek.map((day) => (
              <div key={day} className="flex items-center gap-2">
                <label className={`relative inline-flex items-center ${recurrenceFrequency === "Daily" ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}>
                  <input
                    type="checkbox"
                    checked={selectedDays[day]}
                    onChange={() => handleDayToggle(day)}
                    disabled={recurrenceFrequency === "Daily"}
                    className="sr-only peer"
                  />
                  <div className={`w-4 h-4 rounded border-2 border-[#F1F1F2] peer-checked:bg-[#FD7657] peer-checked:border-[#FD7657] flex items-center justify-center transition-all ${
                    recurrenceFrequency === "Daily" ? "" : "hover:border-gray-400"
                  }`}>
                    {selectedDays[day] && (
                      <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                </label>
                <label className="text-[#9B9B9B] text-xs font-[300]">{day.substring(0, 3)}</label>
              </div>
            ))}
          </div>
        </div>

        {/* Items/Discount header row: Label and Search */}
        <div className="mb-2 flex w-full min-w-0 flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
          <div className="min-w-0 shrink-0 font-sans text-base font-semibold text-[#1A1A1A] sm:text-[18px]">
            Items/Discount
          </div>
          <div className="relative min-w-0 flex-1" ref={searchBarRef}>
            <SearchBar
              placeholder="Search items to add to event"
              value={searchValue}
              onChange={(value) => {
                setSearchValue(value);
                searchItems(value);
                setShowItemDropdown(!!value);
              }}
              onDebouncedChange={(value) => {
                searchItems(value);
                setShowItemDropdown(!!value);
              }}
            />
            {/* Dropdown list of filtered items */}
            {showItemDropdown && searchValue && filteredItems.length > 0 && (
              <div className="absolute z-50 w-full mt-2 bg-white border border-[#F1F1F2] rounded-[8px] shadow-lg max-h-60 overflow-y-auto">
                {filteredItems.map((item: MenuItem) => {
                  const isAlreadyAdded = eventItems.some(
                    (eventItem) => eventItem.correlationId === item.correlationId
                  );
                  
                  return (
                    <div
                      key={item.correlationId}
                      onClick={() => {
                        // Check if item already exists in the table
                        const isAlreadyAdded = eventItems.some(
                          (eventItem) => eventItem.correlationId === item.correlationId
                        );
                        
                        if (isAlreadyAdded) {
                          // Item already exists, don't add it again
                          return;
                        }
                        
                        // Add item to event items table
                        // item is a MenuItem, not an EventItem, so it has priceCents
                        const menuItemPriceCents = typeof item.priceCents === 'string' 
                          ? parseInt(item.priceCents) 
                          : (item.priceCents || 0);
                        const newItem = {
                          id: Date.now(),
                          itemName: item.englishName || "",
                          basePrice: menuItemPriceCents / 100,
                          discountType: "Fixed",
                          discountPercentage: "",
                          discountFixedPrice: "",
                          finalPrice: menuItemPriceCents / 100,
                          correlationId: item.correlationId,
                        };
                        setEventItems([...eventItems, newItem]);
                        setSearchValue("");
                        setShowItemDropdown(false);
                      }}
                      className={`px-4 py-2 cursor-pointer text-sm border-b border-[#F1F1F2] last:border-b-0 ${
                        isAlreadyAdded
                          ? "bg-gray-100 text-[#9B9B9B] cursor-not-allowed opacity-50"
                          : "hover:bg-gray-100 text-[#252525]"
                      }`}
                      title={isAlreadyAdded ? "Item already added" : ""}
                    >
                      {item.englishName || "Unnamed Item"}
                      {isAlreadyAdded && (
                        <span className="ml-2 text-xs text-[#9B9B9B]">(Already added)</span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            {/* No results message */}
            {showItemDropdown && searchValue && filteredItems.length === 0 && (
              <div className="absolute z-50 w-full mt-2 bg-white border border-[#F1F1F2] rounded-[8px] shadow-lg p-4 text-sm text-[#9B9B9B]">
                No items found
              </div>
            )}
          </div>
        </div>

        {/* Items/Discount box */}
        <div className="mb-2 w-full min-w-0 max-w-full box-border rounded-[8px] border border-[#F1F1F2] p-2 sm:p-4 min-h-[200px] max-h-[min(380px,45dvh)] sm:max-h-[380px] flex flex-col overflow-hidden">
          <div className="min-h-0 flex-1 w-full overflow-auto overscroll-x-contain">
            <table className="w-full min-w-[640px] text-left table-fixed" style={{ tableLayout: 'fixed' }}>
              <thead>
                <tr className="bg-white">
                  <th className="text-xs text-[#9B9B9B] pt-2 pb-4 px-2 border-b border-[#F1F1F2] whitespace-nowrap text-left" style={{ width: '160px' }}>
                    Item Name
                  </th>
                  <th className="text-xs text-[#9B9B9B] pt-2 pb-4 px-2 border-b border-[#F1F1F2] whitespace-nowrap text-left" style={{ width: '100px' }}>
                    Base Price
                  </th>
                  <th className="text-xs text-[#9B9B9B] pt-2 pb-4 px-2 border-b border-[#F1F1F2] whitespace-nowrap text-left" style={{ width: '140px' }}>
                    Discount Type
                  </th>
                  <th className="text-xs text-[#9B9B9B] pt-2 pb-4 px-2 border-b border-[#F1F1F2] whitespace-nowrap text-left" style={{ width: '180px' }}>
                    Discount Percentage (%)
                  </th>
                  <th className="text-xs text-[#9B9B9B] pt-2 pb-4 px-2 border-b border-[#F1F1F2] whitespace-nowrap text-left" style={{ width: '180px' }}>
                    Discount Fixed Price ($)
                  </th>
                  <th className="text-xs text-[#9B9B9B] pt-2 pb-4 px-2 border-b border-[#F1F1F2] whitespace-nowrap text-left" style={{ width: '100px' }}>
                    <div className="flex items-center gap-2">
                      Final Price
                      <div 
                        className="relative inline-block"
                        onMouseEnter={(e) => {
                          const rect = e.currentTarget.getBoundingClientRect();
                          const tooltipMaxWidth = 280; // max width of tooltip
                          const tooltipHalfWidth = tooltipMaxWidth / 2;
                          
                          // Find the modal content container - look for the div with px-8 py-6 classes
                          let formContainer: HTMLElement | null = null;
                          let parent: HTMLElement | null = e.currentTarget as HTMLElement;
                          
                          // Traverse up to find the modal content container
                          while (parent && parent !== document.body) {
                            if (parent.classList && parent.classList.contains('px-8') && parent.classList.contains('py-6')) {
                              formContainer = parent;
                              break;
                            }
                            // Also check for role="dialog" parent
                            if (parent.getAttribute && parent.getAttribute('role') === 'dialog') {
                              const contentDiv = parent.querySelector('[class*="px-8"][class*="py-6"]');
                              if (contentDiv) {
                                formContainer = contentDiv as HTMLElement;
                                break;
                              }
                            }
                            parent = parent.parentElement;
                          }
                          
                          let formLeft = 20;
                          let formRight = window.innerWidth - 20;
                          
                          if (formContainer) {
                            const formRect = formContainer.getBoundingClientRect();
                            // Modal has width="1106px" and padding px-8 (32px each side)
                            // So content area is: left + 32px to right - 32px
                            formLeft = formRect.left + 32;
                            formRight = formRect.right - 32;
                          } else {
                            // Fallback: try to find by looking for the form's parent containers
                            const modal = e.currentTarget.closest('[role="dialog"]');
                            if (modal) {
                              const modalRect = modal.getBoundingClientRect();
                              // Modal is 1106px wide, centered, with 32px padding
                              formLeft = modalRect.left + 32;
                              formRight = modalRect.right - 32;
                            }
                          }
                          
                          // Calculate left position, ensuring tooltip stays within form boundaries
                          let leftPos = rect.left + rect.width / 2;
                          
                          // First check right boundary (more likely issue since icon is on the right side)
                          const rightBoundary = formRight - 15;
                          const leftBoundary = formLeft + 15;
                          
                          // If tooltip would extend beyond right boundary, adjust
                          if (leftPos + tooltipHalfWidth > rightBoundary) {
                            leftPos = rightBoundary - tooltipHalfWidth;
                            // If that pushes it too far left, constrain to available space
                            if (leftPos < leftBoundary + tooltipHalfWidth) {
                              leftPos = leftBoundary + tooltipHalfWidth;
                              // Calculate available width and adjust tooltip width accordingly
                              const availableWidth = rightBoundary - leftBoundary - 30;
                              if (availableWidth < tooltipMaxWidth) {
                                // Store adjusted width in state or use smaller maxWidth
                              }
                            }
                          }
                          
                          // Check left boundary
                          if (leftPos - tooltipHalfWidth < leftBoundary) {
                            leftPos = leftBoundary + tooltipHalfWidth;
                            // Ensure we don't go beyond right boundary
                            if (leftPos + tooltipHalfWidth > rightBoundary) {
                              const availableWidth = rightBoundary - leftBoundary - 30;
                              leftPos = leftBoundary + Math.min(availableWidth / 2, tooltipHalfWidth);
                            }
                          }
                          
                          setTooltipPosition({
                            x: leftPos,
                            y: rect.top,
                            show: true
                          });
                        }}
                        onMouseLeave={() => {
                          setTooltipPosition({ x: 0, y: 0, show: false });
                        }}
                      >
                        <img 
                          src={infoIcon} 
                          alt="" 
                          width={15} 
                          height={15}
                          className="cursor-help"
                        />
                        {tooltipPosition.show && typeof document !== 'undefined' && createPortal(
                          <div 
                            className="fixed px-3 py-2 text-xs text-[#252525] bg-white border border-[#F1F1F2] rounded-lg shadow-lg pointer-events-none"
                            style={{ 
                              left: `${tooltipPosition.x}px`,
                              top: `${tooltipPosition.y - 5}px`,
                              transform: 'translateX(-50%) translateY(-100%)',
                              zIndex: 99999,
                              maxWidth: '250px',
                              wordWrap: 'break-word',
                              whiteSpace: 'normal',
                              width: 'auto'
                            }}
                          >
                            Final price will automatically update based on the selected discount type and values
                          </div>,
                          document.body
                        )}
                      </div>
                    </div>
                  </th>
                  <th className="text-xs text-[#9B9B9B] pt-2 pb-4 px-2 border-b border-[#F1F1F2] whitespace-nowrap text-left" style={{ width: '60px' }}>
                  </th>
                </tr>
              </thead>
              <tbody>
                {eventItems.map((item, index) => {
                  const calculateFinalPrice = (
                    basePrice: number,
                    discountType: string,
                    discountPercentage: string,
                    discountFixedPrice: string
                  ): number => {
                    let finalPrice = basePrice;
                    
                    if (discountType === "Percentage") {
                      // Remove the percentage-based discount amount from base price
                      const percent = parseFloat(discountPercentage) || 0;
                      const discountAmount = basePrice * (percent / 100);
                      finalPrice = basePrice - discountAmount;
                    } else if (discountType === "Fixed") {
                      // Directly subtract the fixed discount price from base price
                      const fixed = parseFloat(discountFixedPrice) || 0;
                      finalPrice = basePrice - fixed;
                    }
                    
                    // Ensure final price is not negative and round to 2 decimal places
                    finalPrice = Math.max(0, finalPrice);
                    return Math.round(finalPrice * 100) / 100;
                  };

                  const handleDiscountTypeChange = (newType: string) => {
                    const updatedItems = [...eventItems];
                    updatedItems[index] = {
                      ...updatedItems[index],
                      discountType: newType,
                      discountPercentage: newType === "Percentage" ? updatedItems[index].discountPercentage : "",
                      discountFixedPrice: newType === "Fixed" ? (updatedItems[index].discountFixedPrice || "") : "",
                    };
                    // Recalculate final price
                    updatedItems[index].finalPrice = calculateFinalPrice(
                      updatedItems[index].basePrice,
                      newType,
                      newType === "Percentage" ? updatedItems[index].discountPercentage : "",
                      newType === "Fixed" ? (updatedItems[index].discountFixedPrice || "0.00") : ""
                    );
                    setEventItems(updatedItems);
                  };

                  const handleDiscountPercentageChange = (value: string) => {
                    // Only allow numbers and decimal point
                    let numericValue = value.replace(/[^0-9.]/g, '');
                    
                    // Ensure only one decimal point
                    const parts = numericValue.split('.');
                    if (parts.length > 2) {
                      numericValue = parts[0] + '.' + parts.slice(1).join('');
                    }
                    
                    // Restrict to 0-100 range
                    const numValue = parseFloat(numericValue);
                    if (!isNaN(numValue)) {
                      if (numValue > 100) {
                        numericValue = '100';
                      } else if (numValue < 0) {
                        numericValue = '0';
                      }
                    }
                    
                    const updatedItems = [...eventItems];
                    updatedItems[index].discountPercentage = numericValue;
                    updatedItems[index].finalPrice = calculateFinalPrice(
                      updatedItems[index].basePrice,
                      updatedItems[index].discountType,
                      numericValue,
                      ""
                    );
                    setEventItems(updatedItems);
                  };

                  const handleDiscountFixedPriceChange = (value: string) => {
                    // Only allow numbers and decimal point
                    let numericValue = value.replace(/[^0-9.]/g, '');
                    
                    // Ensure only one decimal point
                    const parts = numericValue.split('.');
                    if (parts.length > 2) {
                      numericValue = parts[0] + '.' + parts.slice(1).join('');
                    }
                    
                    // Limit to 2 decimal places while typing
                    if (parts.length === 2 && parts[1].length > 2) {
                      numericValue = parts[0] + '.' + parts[1].substring(0, 2);
                    }
                    
                    const updatedItems = [...eventItems];
                    updatedItems[index].discountFixedPrice = numericValue;
                    updatedItems[index].finalPrice = calculateFinalPrice(
                      updatedItems[index].basePrice,
                      updatedItems[index].discountType,
                      "",
                      numericValue
                    );
                    setEventItems(updatedItems);
                  };

                  const handleDiscountFixedPriceBlur = () => {
                    const updatedItems = [...eventItems];
                    let currentValue = updatedItems[index].discountFixedPrice;
                    
                    // Remove any non-numeric characters except decimal point
                    currentValue = currentValue ? currentValue.replace(/[^0-9.]/g, '') : '';
                    
                    // Format to 2 decimal places if there's a value
                    if (currentValue && currentValue.trim() !== '' && currentValue !== '.') {
                      const numValue = parseFloat(currentValue);
                      if (!isNaN(numValue)) {
                        const formattedValue = numValue.toFixed(2);
                        updatedItems[index].discountFixedPrice = formattedValue;
                        updatedItems[index].finalPrice = calculateFinalPrice(
                          updatedItems[index].basePrice,
                          updatedItems[index].discountType,
                          "",
                          formattedValue
                        );
                        setEventItems(updatedItems);
                      } else {
                        // Invalid number, set to "0.00"
                        updatedItems[index].discountFixedPrice = "0.00";
                        updatedItems[index].finalPrice = calculateFinalPrice(
                          updatedItems[index].basePrice,
                          updatedItems[index].discountType,
                          "",
                          "0.00"
                        );
                        setEventItems(updatedItems);
                      }
                    } else {
                      // If empty or invalid, set to "0.00"
                      updatedItems[index].discountFixedPrice = "0.00";
                      updatedItems[index].finalPrice = calculateFinalPrice(
                        updatedItems[index].basePrice,
                        updatedItems[index].discountType,
                        "",
                        "0.00"
                      );
                      setEventItems(updatedItems);
                    }
                  };

                  const rowColor = index % 2 === 0 ? "#FCFCFC" : "#FFFFFF";
                  const textColor = "#252525";
                  
                  return (
                    <tr key={item.id} style={{ backgroundColor: rowColor }}>
                      <td className="px-2 py-1 text-xs text-left" style={{ color: textColor }}>
                        {item.itemName}
                      </td>
                      <td className="px-2 py-1 text-xs text-left" style={{ color: textColor }}>
                        ${item.basePrice.toFixed(2)}
                      </td>
                      <td className="px-2 py-1 text-left">
                        <div className="w-full">
                          <CustomSelect
                            options={[
                              { value: "Fixed", label: "Fixed" },
                              { value: "Percentage", label: "Percentage" },
                            ]}
                            value={item.discountType}
                            onChange={handleDiscountTypeChange}
                            placeholder="Select type"
                            label=""
                          />
                        </div>
                      </td>
                      <td className="px-2 py-1 text-left">
                        <Input
                          type={InputTypeEnum.TEXT}
                          as={InputAsEnum.INPUT}
                          name={`discountPercentage-${item.id}`}
                          value={item.discountPercentage}
                          onChange={(e) => handleDiscountPercentageChange(e.target.value)}
                          disabled={item.discountType === "Fixed"}
                          className="h-[42px]"
                          placeholder="0"
                        />
                      </td>
                      <td className="px-2 py-1 text-left">
                        <Input
                          type={InputTypeEnum.TEXT}
                          as={InputAsEnum.INPUT}
                          name={`discountFixedPrice-${item.id}`}
                          value={item.discountFixedPrice}
                          onChange={(e) => handleDiscountFixedPriceChange(e.target.value)}
                          onBlur={handleDiscountFixedPriceBlur}
                          disabled={item.discountType === "Percentage"}
                          className="h-[42px]"
                          placeholder="0.00"
                        />
                      </td>
                      <td className="px-2 py-1 text-xs text-left" style={{ color: textColor }}>
                        ${item.finalPrice.toFixed(2)}
                      </td>
                      <td className="px-2 py-1 text-left">
                        <button
                          onClick={() => {
                            setEventItems(eventItems.filter((eventItem) => eventItem.id !== item.id));
                          }}
                          className="h-[32px] w-[32px] flex items-center justify-center cursor-pointer hover:opacity-80 transition-opacity bg-transparent border-none p-0"
                          aria-label={`Delete ${item.itemName}`}
                          type="button"
                        >
                          <img
                            className="h-full w-full cursor-pointer"
                            src={ICONS.DeleteIcon}
                            alt="Delete"
                          />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        </div>
        {/* Cancel and Save buttons */}
        <div className="mt-6 mb-2 flex w-full min-w-0 shrink-0 flex-wrap justify-end gap-3">
          <Button
            variant="secondary"
            onClick={() => {
              // Reset form and event items
              setEventItems([]);
              setSearchValue("");
              setSelectedDays({
                Monday: false,
                Tuesday: false,
                Wednesday: false,
                Thursday: false,
                Friday: false,
                Saturday: false,
                Sunday: false,
              });
              setRecurrenceFrequency("Daily");
              handleCloseEventModal();
              formik.resetForm();
            }}
            type="button"
          >
            Cancel
          </Button>
          <Button
            onClick={() => formik.handleSubmit()}
            isLoading={isLoadingEvent}
            disabled={!formik.isValid || isLoadingEvent}
            type="button"
          >
            {buttonText}
          </Button>
        </div>
      </div>
  );
};

export default EventForm;
